import type {
  AnalyticsRangeKey,
  AnalyticsRangeSelection,
} from '@shared/api-client';
import {
  ANALYTICS_RANGE_LABELS,
  analyticsRangeLabel,
} from '@shared/api-client/analytics-range';
import * as Popover from '@radix-ui/react-popover';
import { CalendarDays, Check, ChevronDown, ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const GROUPS: AnalyticsRangeKey[][] = [
  ['today', 'yesterday', '24h'],
  ['7d', '30d', '90d'],
  ['month', 'last-month', 'year', '12mo'],
  ['all', 'custom'],
];

export const AnalyticsRangeSelect = ({
  value,
  onValueChange,
}: {
  value: AnalyticsRangeSelection;
  onValueChange: (value: AnalyticsRangeSelection) => void;
}) => {
  const [open, setOpen] = React.useState(false);
  const [custom, setCustom] = React.useState(false);
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const id = React.useId();
  const today = new Date().toISOString().slice(0, 10);
  const error =
    from && to
      ? from > to
        ? 'End date must be on or after start date.'
        : to > today
          ? 'Choose dates up to today.'
          : from < '1970-01-01'
            ? 'Choose dates on or after 1 January 1970.'
            : ''
      : '';
  const choose = (key: AnalyticsRangeKey) => {
    if (key === 'custom') {
      setFrom(
        value.from ??
          new Date(Date.now() - 6 * 86_400_000).toISOString().slice(0, 10),
      );
      setTo(value.to ?? today);
      setCustom(true);
    } else {
      onValueChange({ key });
      setOpen(false);
    }
  };
  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setCustom(false);
        }
      }}
    >
      <Popover.Trigger asChild>
        <Button
          variant="secondary"
          aria-label={`Analytics date range: ${analyticsRangeLabel(value)}`}
          className="max-w-full justify-between gap-2 font-normal"
        >
          <CalendarDays
            className="size-4 shrink-0 text-ink-muted"
            aria-hidden="true"
          />
          <span className="truncate">{analyticsRangeLabel(value)}</span>
          <ChevronDown
            className="size-4 shrink-0 text-ink-muted"
            aria-hidden="true"
          />
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          collisionPadding={12}
          aria-label="Date range"
          className={cn(
            'z-50 max-h-[var(--radix-popover-content-available-height)] max-w-[calc(100vw-24px)] overflow-y-auto rounded-lg border border-border-strong bg-surface shadow-xl',
            custom ? 'w-80 p-4' : 'w-60 p-1',
          )}
        >
          {custom ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (!from || !to || error) {
                  return;
                }
                onValueChange({ key: 'custom', from, to });
                setOpen(false);
              }}
            >
              <button
                type="button"
                onClick={() => setCustom(false)}
                className="mb-4 flex items-center gap-2 text-sm font-medium text-ink"
              >
                <ArrowLeft className="size-4" aria-hidden="true" />
                Custom range
              </button>
              <div className="space-y-3">
                {[
                  {
                    name: 'from',
                    label: 'Start date',
                    value: from,
                    set: setFrom,
                  },
                  { name: 'to', label: 'End date', value: to, set: setTo },
                ].map((field) => (
                  <div key={field.name}>
                    <label
                      htmlFor={`${id}-${field.name}`}
                      className="mb-1.5 block text-xs text-ink-muted"
                    >
                      {field.label}
                    </label>
                    <input
                      id={`${id}-${field.name}`}
                      type="date"
                      required
                      min="1970-01-01"
                      max={today}
                      value={field.value}
                      onChange={(event) => field.set(event.target.value)}
                      onInput={(event) => field.set(event.currentTarget.value)}
                      aria-describedby={error ? `${id}-error` : `${id}-hint`}
                      aria-invalid={Boolean(error)}
                      className="h-10 w-full min-w-0 rounded-md border border-border-strong bg-surface px-3 text-sm text-ink [color-scheme:dark]"
                    />
                  </div>
                ))}
              </div>
              <p id={`${id}-hint`} className="mt-3 text-xs text-ink-muted">
                UTC · Includes both dates.
              </p>
              {error && (
                <p
                  id={`${id}-error`}
                  role="alert"
                  className="mt-2 text-xs text-critical"
                >
                  {error}
                </p>
              )}
              <div className="mt-4 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={!from || !to || Boolean(error)}>
                  Apply
                </Button>
              </div>
            </form>
          ) : (
            <>
              {GROUPS.map((group, index) => (
                <div
                  key={index}
                  className={cn('py-1', index > 0 && 'border-t border-border')}
                >
                  {group.map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => choose(key)}
                      aria-pressed={value.key === key}
                      className={cn(
                        'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm text-ink hover:bg-surface-muted focus-visible:bg-surface-muted',
                        value.key === key && 'bg-surface-muted font-medium',
                      )}
                    >
                      {ANALYTICS_RANGE_LABELS[key]}
                      {value.key === key && (
                        <Check
                          className="size-4 text-accent"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  ))}
                </div>
              ))}
              <p className="border-t border-border px-3 py-2 text-xs text-ink-muted">
                All dates use UTC
              </p>
            </>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
